import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import TourTooltip, { type TourTooltipHandle } from './TourTooltip';
import {
    getSpotlightRect,
    isVisualViewportZoomed,
    placeAboveBottomNav,
    placeTooltip,
    readVisualViewportBox,
    resolveTourTarget,
    resolveTourTargets,
    roundedRectPath,
    scrollTargetIntoView,
    type Rect,
    type SpotlightShape,
    type VisualViewportBox,
} from './tourDom';
import { scrollToElement } from '../../utils/scrollToElement';
import type { TourStep } from './tours/types';

interface TourOverlayProps {
    steps: TourStep[];
    stepIndex: number;
    onNext: () => void;
    onSkip: () => void;
    onAbort?: () => void;
    onPrimaryAction?: (step: TourStep) => void;
}

const EST_W = 320;
const EST_H = 280;

/** Layout-space shapes → coords local to the visual-viewport shell. */
function toLocalShapes(shapes: SpotlightShape[], vv: VisualViewportBox): SpotlightShape[] {
    return shapes.map((r) => ({
        ...r,
        top: r.top - vv.offsetTop,
        left: r.left - vv.offsetLeft,
        bottom: r.bottom - vv.offsetTop,
        right: r.right - vv.offsetLeft,
    }));
}

const TourOverlay = ({ steps, stepIndex, onNext, onSkip, onAbort, onPrimaryAction }: TourOverlayProps) => {
    const step = steps[stepIndex];
    /** One or more spotlight holes (primary first), including real button corner radii. */
    const [spotShapes, setSpotShapes] = useState<SpotlightShape[]>([]);
    const [tooltipPos, setTooltipPos] = useState<{ top: number; left: number }>({ top: 16, left: 16 });
    const [missing, setMissing] = useState(false);
    /** Soft hint when browser still reports pinch-zoom after best-effort normalize. */
    const [showZoomHint, setShowZoomHint] = useState(false);
    /** Shell must track visual viewport or pinch-zoom leaves undimmed page edges. */
    const [vvBox, setVvBox] = useState<VisualViewportBox>(() =>
        typeof window !== 'undefined'
            ? readVisualViewportBox()
            : { offsetTop: 0, offsetLeft: 0, width: 390, height: 844, scale: 1 }
    );
    const tooltipRef = useRef<TourTooltipHandle>(null);
    const primaryElRef = useRef<HTMLElement | null>(null);
    const allElsRef = useRef<HTMLElement[]>([]);
    const measureGen = useRef(0);

    const placeForStep = useCallback(
        (primaryRect: Rect, w: number, h: number) => {
            if (!step) return;
            if (step.anchor === 'bottom-nav') {
                const anchored = placeAboveBottomNav(w, h, step.anchorGapPx ?? 8, primaryRect);
                if (anchored) {
                    setTooltipPos({ top: anchored.top, left: anchored.left });
                    return;
                }
            }
            const placed = placeTooltip(primaryRect, w, h, step.placement ?? 'auto');
            setTooltipPos({ top: placed.top, left: placed.left });
        },
        [step]
    );

    const applyMeasure = useCallback(
        (els: HTMLElement[]) => {
            if (!step || els.length === 0) return;
            const pad = step.spotlightPadding ?? 4;
            const shapes = els
                .map((el) => getSpotlightRect(el, pad))
                .filter((r) => r.width > 4 && r.height > 4);
            if (shapes.length === 0) return;
            setSpotShapes(shapes);
            const primary = shapes[0];
            placeForStep(primary, EST_W, EST_H);
            const size = tooltipRef.current?.getSize();
            if (size && size.height >= 40) {
                placeForStep(primary, size.width, size.height);
            }
        },
        [step, placeForStep]
    );

    const measure = useCallback(() => {
        if (!step) return;
        const gen = ++measureGen.current;

        const els = step.highlightAllTargets
            ? resolveTourTargets(step.target)
            : (() => {
                  const one = resolveTourTarget(step.target);
                  return one ? [one] : [];
              })();

        if (els.length === 0) {
            setMissing(true);
            setSpotShapes([]);
            primaryElRef.current = null;
            allElsRef.current = [];
            return;
        }

        setMissing(false);
        primaryElRef.current = els[0];
        allElsRef.current = els;

        // Multi-highlight (Find + recommendations): pin primary near top of free band
        // so the second ring has room below. Single-target steps keep centered scroll.
        if (step.highlightAllTargets && els.length > 1) {
            scrollToElement(els[0], { behavior: 'auto', block: 'start' });
        } else {
            scrollTargetIntoView(els[0]);
        }

        const run = () => {
            if (measureGen.current !== gen) return;
            const live = allElsRef.current.filter((el) => el.isConnected);
            if (live.length === 0) return;
            applyMeasure(live);
            setShowZoomHint(isVisualViewportZoomed());
        };

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                run();
                window.setTimeout(run, 50);
            });
        });
    }, [step, applyMeasure]);

    useLayoutEffect(() => {
        if (spotShapes.length === 0 || !step) return;
        let n = 0;
        let raf = 0;
        const apply = () => {
            const live = allElsRef.current.filter((el) => el.isConnected);
            if (live.length === 0) return;
            const size = tooltipRef.current?.getSize();
            if (size && size.height >= 40) {
                const pad = step.spotlightPadding ?? 4;
                const shapes = live.map((el) => getSpotlightRect(el, pad));
                setSpotShapes(shapes);
                placeForStep(shapes[0], size.width, size.height);
            }
            n += 1;
            if (n < 3) raf = requestAnimationFrame(apply);
        };
        raf = requestAnimationFrame(apply);
        return () => cancelAnimationFrame(raf);
    }, [spotShapes.length, step, stepIndex, placeForStep]);

    useLayoutEffect(() => {
        measure();
    }, [measure, stepIndex]);

    useEffect(() => {
        // Pinch-zoom / pan: keep dim shell + holes locked to the *visible* viewport
        const onViewport = () => {
            setVvBox(readVisualViewportBox());
            measure();
        };
        onViewport();
        window.addEventListener('resize', onViewport);
        window.visualViewport?.addEventListener('resize', onViewport);
        window.visualViewport?.addEventListener('scroll', onViewport);
        return () => {
            window.removeEventListener('resize', onViewport);
            window.visualViewport?.removeEventListener('resize', onViewport);
            window.visualViewport?.removeEventListener('scroll', onViewport);
        };
    }, [measure]);

    useEffect(() => {
        const block = (e: Event) => {
            const t = e.target;
            if (t instanceof Element && t.closest('[role="dialog"]')) return;
            // Allow pinch-out if the page is still zoomed (hint may ask user to reset)
            if (e instanceof TouchEvent && e.touches.length > 1) return;
            e.preventDefault();
        };
        document.addEventListener('wheel', block, { passive: false });
        document.addEventListener('touchmove', block, { passive: false });
        return () => {
            document.removeEventListener('wheel', block);
            document.removeEventListener('touchmove', block);
        };
    }, []);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onSkip();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onSkip]);

    useEffect(() => {
        if (!missing || !step) return;
        const retry = window.setTimeout(() => measure(), 250);
        const skip = window.setTimeout(() => {
            if (stepIndex < steps.length - 1) onNext();
            else (onAbort ?? onSkip)();
        }, 1200);
        return () => {
            window.clearTimeout(retry);
            window.clearTimeout(skip);
        };
    }, [missing, step, stepIndex, steps.length, onNext, onSkip, onAbort, measure]);

    if (!step) return null;

    const maskId = `tour-mask-${step.id}`;
    // Shell = visual viewport (what the user actually sees when pinch-zoomed).
    // Layout getBoundingClientRect → subtract offset so holes align inside the shell.
    const vw = Math.max(1, vvBox.width);
    const vh = Math.max(1, vvBox.height);
    const localShapes = toLocalShapes(spotShapes, vvBox);

    return createPortal(
        <div
            className="z-[10030]"
            data-product-tour-overlay
            style={{
                position: 'fixed',
                top: vvBox.offsetTop,
                left: vvBox.offsetLeft,
                width: vw,
                height: vh,
            }}
        >
            <div className="absolute inset-0 z-[10030]" aria-hidden onClick={onSkip} />

            {localShapes.length > 0 ? (
                <>
                    {/* Dim + hole(s) shaped like the real control (rounded-l-xl, full rounded, etc.) */}
                    <svg
                        className="pointer-events-none absolute inset-0 z-[10031]"
                        width={vw}
                        height={vh}
                        viewBox={`0 0 ${vw} ${vh}`}
                        preserveAspectRatio="none"
                        aria-hidden
                    >
                        <defs>
                            <mask id={maskId} maskUnits="userSpaceOnUse" x={0} y={0} width={vw} height={vh}>
                                <rect x={0} y={0} width={vw} height={vh} fill="white" />
                                {localShapes.map((r, i) => (
                                    <path
                                        key={i}
                                        d={roundedRectPath(r.left, r.top, r.width, r.height, r.radii)}
                                        fill="black"
                                    />
                                ))}
                            </mask>
                        </defs>
                        <rect
                            x={0}
                            y={0}
                            width={vw}
                            height={vh}
                            fill="rgba(0,0,0,0.4)"
                            mask={`url(#${maskId})`}
                        />
                    </svg>
                    {localShapes.map((r, i) => (
                        <div
                            key={i}
                            className="pointer-events-none absolute z-[10032] border-2 border-[var(--accent-color)] motion-safe:animate-pulse"
                            style={{
                                top: r.top,
                                left: r.left,
                                width: r.width,
                                height: r.height,
                                borderRadius: r.borderRadius,
                                boxSizing: 'border-box',
                            }}
                            aria-hidden
                        />
                    ))}
                </>
            ) : (
                <div className="pointer-events-none absolute inset-0 z-[10031] bg-black/40" aria-hidden />
            )}

            <div
                className="relative z-[10033]"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
            >
                <TourTooltip
                    ref={tooltipRef}
                    step={step}
                    stepIndex={stepIndex}
                    stepCount={steps.length}
                    style={{
                        // placeTooltip returns layout coords; shell is visual-viewport local
                        top: tooltipPos.top - vvBox.offsetTop,
                        left: tooltipPos.left - vvBox.offsetLeft,
                    }}
                    zoomHint={showZoomHint && stepIndex === 0}
                    onNext={onNext}
                    onSkip={onSkip}
                    onPrimaryAction={
                        step.primaryAction ? () => onPrimaryAction?.(step) : undefined
                    }
                />
            </div>
        </div>,
        document.body
    );
};

export default TourOverlay;
